import { css } from '@firebolt-dev/css'
import { useState } from 'react'

const CONFIG = {
  converterUrl: 'https://mml.roustan.xyz/api/convert-url',
  exampleMML: 'https://arweave.net/9IAL3hGJLIgO9m9bxqLuqwY78-Wqu7padl3xOZF7VvM/2.mml',
}

// Hash file using SHA-256 (same as Hyperfy's utils-client.js)
async function hashFile(file) {
  const buf = await file.arrayBuffer()
  const hashBuf = await crypto.subtle.digest('SHA-256', buf)
  const hash = Array.from(new Uint8Array(hashBuf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
  return hash
}

export function MMLAvatarLoader({ world }) {
  const [isOpen, setIsOpen] = useState(false)
  const [mmlUrl, setMmlUrl] = useState('')
  const [status, setStatus] = useState('')
  const [statusColor, setStatusColor] = useState('#44ff44')
  const [isLoading, setIsLoading] = useState(false)

  const handleButtonClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('MML button clicked!')
    setIsOpen(true)
  }

  const handleLoad = async () => {
    if (isLoading || !mmlUrl.trim()) {
      if (!mmlUrl.trim()) {
        setStatus('Please enter an MML URL')
        setStatusColor('#ff4444')
      }
      return
    }

    let url = mmlUrl.trim()
    if (!url.startsWith('http')) {
      url = `https://arweave.net/${url}`
    }
    if (!url.includes('.mml') && !/arweave\.(net|dev)/i.test(url)) {
      setStatus('Enter a .mml URL or Arweave link (tx ID or full URL)')
      setStatusColor('#ff4444')
      return
    }

    setIsLoading(true)
    setStatus('Converting MML to VRM...')
    setStatusColor('#4a9eff')

    try {
      const response = await fetch(`${CONFIG.converterUrl}?url=${encodeURIComponent(url)}`)
      
      if (!response.ok) {
        throw new Error(`Converter failed: ${response.status}`)
      }

      const vrmBlob = await response.blob()
      
      setStatus('VRM received! Computing hash...')
      setStatusColor('#44ff44')

      // Create temporary file to hash
      const tempFile = new File([vrmBlob], 'temp.vrm', { type: 'model/gltf-binary' })
      
      // Compute SHA-256 hash (same as Hyperfy's upload system)
      const hash = await hashFile(tempFile)
      const filename = `${hash}.vrm`
      
      // Create file with proper hashed filename
      const fileObj = new File([vrmBlob], filename, { type: 'model/gltf-binary' })
      
      // Create canonical asset URL using the hash
      const assetUrl = `asset://${filename}`
      
      setStatus('Opening avatar preview...')
      
      // Cache file locally so this client can load it
      world.loader.insert('avatar', assetUrl, fileObj)
      
      // Emit avatar event with proper onEquip callback
      world.emit('avatar', { 
        file: fileObj,
        url: assetUrl,
        hash: hash,
        canPlace: false,
        onEquip: async () => {
          // Close pane
          world.emit('avatar', null)
          
          // Get player and save previous avatar
          const player = world.entities.player
          const prevUrl = player.data.avatar
          
          // Update locally
          player.modify({ avatar: assetUrl, sessionAvatar: null })
          
          // Upload file to server
          try {
            await world.network.upload(fileObj)
          } catch (err) {
            console.error('Avatar upload error:', err)
            // Revert on error
            player.modify({ avatar: prevUrl })
            return
          }
          
          // Check if player changed avatar while uploading
          if (player.data.avatar !== assetUrl) {
            return
          }
          
          // Update for everyone
          world.network.send('entityModified', {
            id: player.data.id,
            avatar: assetUrl,
          })
          
          console.log('Avatar equipped successfully!')
        }
      })

      setTimeout(() => {
        setIsOpen(false)
        setMmlUrl('')
        setStatus('')
      }, 2000)

    } catch (error) {
      console.error('MML Avatar load error:', error)
      setStatus(`Error: ${error.message}`)
      setStatusColor('#ff4444')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLoad()
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setMmlUrl('')
    setStatus('')
  }

  return (
    <>
      {/* Button - positioned to align with emote.hyp button */}
      {/* emote.hyp: offset [-30, -120], size 50x50 */}
      {/* MML: centered above, size 40x40 (2.5rem) */}
      <div
        onClick={handleButtonClick}
        css={css`
          pointer-events: auto;
          position: fixed;
          width: 40px;
          height: 40px;
          bottom: 180px;
          right: 35px;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          color: white;
          z-index: 1000;
        `}
        title="Load Avatar from MML"
      >
        MML
      </div>

      {/* Modal */}
      {isOpen && (
        <div
          css={css`
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            pointer-events: auto;
          `}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose()
          }}
        >
          <div
            css={css`
              background: rgba(11, 10, 21, 0.95);
              border-radius: 1rem;
              padding: 1.5rem;
              max-width: 400px;
              width: 90%;
              border: 1px solid rgba(255, 255, 255, 0.1);
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              css={css`
                color: white;
                margin: 0 0 0.5rem 0;
                font-size: 1.25rem;
                text-align: center;
              `}
            >
              Load Avatar from MML
            </h2>

            <p
              css={css`
                color: rgba(255, 255, 255, 0.7);
                margin: 0 0 1rem 0;
                font-size: 0.875rem;
                text-align: center;
              `}
            >
              Enter an MML URL to convert and load as your avatar
            </p>

            <input
              type="text"
              value={mmlUrl}
              onChange={(e) => setMmlUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Paste MML URL here..."
              autoFocus
              css={css`
                width: 100%;
                padding: 0.75rem;
                border: 1px solid rgba(255, 255, 255, 0.2);
                border-radius: 0.5rem;
                background: rgba(255, 255, 255, 0.1);
                color: white;
                font-size: 0.875rem;
                margin-bottom: 0.75rem;
                box-sizing: border-box;
                font-family: monospace;
                &:focus {
                  outline: none;
                  border-color: rgba(255, 255, 255, 0.4);
                }
                &::placeholder {
                  color: rgba(255, 255, 255, 0.4);
                }
              `}
            />

            {status && (
              <div
                css={css`
                  text-align: center;
                  margin-bottom: 0.75rem;
                  font-size: 0.875rem;
                  padding: 0.5rem;
                  border-radius: 0.5rem;
                  background: rgba(0, 0, 0, 0.3);
                `}
                style={{ color: statusColor }}
              >
                {status}
              </div>
            )}

            <div
              css={css`
                display: flex;
                gap: 0.5rem;
                justify-content: center;
              `}
            >
              <button
                type="button"
                onClick={handleLoad}
                disabled={isLoading}
                css={css`
                  background: rgba(74, 158, 255, 0.8);
                  color: white;
                  border: none;
                  padding: 0.75rem 1.5rem;
                  border-radius: 0.5rem;
                  font-size: 0.9rem;
                  font-weight: 500;
                  cursor: pointer;
                  flex: 1;
                  &:hover:not(:disabled) {
                    background: rgba(74, 158, 255, 1);
                  }
                  &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }
                `}
              >
                {isLoading ? 'Loading...' : 'Load Avatar'}
              </button>

              <button
                type="button"
                onClick={handleClose}
                css={css`
                  background: rgba(255, 255, 255, 0.1);
                  color: white;
                  border: 1px solid rgba(255, 255, 255, 0.2);
                  padding: 0.75rem 1rem;
                  border-radius: 0.5rem;
                  font-size: 0.9rem;
                  cursor: pointer;
                  &:hover {
                    background: rgba(255, 255, 255, 0.2);
                  }
                `}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
